import { NumericFormat as NumberFormat } from 'react-number-format';
import { Link, useParams } from 'react-router';
import ImageCircularAvatar from '@/components/UI/Avatar/ImageCircularAvatar';
import Badge from '@/components/UI/Badge';
import { CurrencyFormatDisplay } from '@/components/UI/NumberFormatDisplay/CurrencyFormatDisplay';
import { useProject } from '../../../../../../../hooks/ReactQueryHooks/useProjectQuery';
import { convertEnumToText, getInitials } from '../../../../../../../utils';
import ProjectSettings from './ProjectSettings';

const ProjectInfo = () => {
  const { projectId } = useParams<string>();
  const fetchProject = useProject({ projectId: parseInt(projectId!, 10) });
  const project = fetchProject?.data?.data;

  return (
    <>
      {project && (
        <div className='flex flex-col justify-between py-5 w-full space-y-4'>
          <div className='w-full relative flex gap-2 border-b pb-5'>
            <ProjectSettings />
            <div className='flex flex-col gap-4 flex-grow ml-6'>
              <div>
                <section className='flex gap-2 items-center'>
                  <h2 className='font-bold m-0 break-all'>{project.name}</h2>
                  <div className='hidden xl:block space-x-2'>
                    <Badge status={project.stage ? project.stage : 'neutral'} text={convertEnumToText(project.status)} />
                    {project?.archived && <Badge badgeType='tertiary' text='Archived' />}
                    {project?.departmentLedProject && <Badge badgeType='neutral' text='Department Led' />}
                  </div>
                </section>
                <p className='text-neutralColor-normal'>
                  {project.address}, {project.city}, {project.state}, {project.zipCode}
                </p>
                <div className='xl:hidden'>
                  <Badge status={project.stage ? project.stage : 'neutral'} text={convertEnumToText(project.status)} />
                  {project?.archived && <Badge badgeType='tertiary' text='Archived' />}
                </div>
              </div>
              <dl className='w-full flex justify-between'>
                <div>
                  <dt className='text-neutralColor-normal'>Project Stage</dt>
                  <dd className='font-semibold'>{project.stage ? convertEnumToText(project.stage) : 'Not Selected'}</dd>
                </div>
                <div>
                  <dt className='text-neutralColor-normal'>Neighborhood</dt>
                  <dd className='font-semibold'>{project.neighborhood ? project.neighborhood : '-'}</dd>
                </div>
                <div>
                  <dt className='text-neutralColor-normal'>Project Type</dt>
                  <dd className='font-semibold'>{project.projectType}</dd>
                </div>
                {project?.units > 0 && (
                  <div>
                    <dt className='text-neutralColor-normal'>Units</dt>
                    <dd className='font-semibold'>
                      <NumberFormat value={project.units} displayType={'text'} thousandSeparator={true} />
                    </dd>
                  </div>
                )}
                {!!project?.avgSalesPrice && project?.avgSalesPrice > 0 && (
                  <div>
                    <dt className='text-neutralColor-normal'>Average Sales Price</dt>
                    <dd className='font-semibold'>
                      <CurrencyFormatDisplay value={project.avgSalesPrice} prefix='$' />
                    </dd>
                  </div>
                )}
                <div>
                  <dt className='text-neutralColor-normal'>Construction Type</dt>
                  <dd className='font-semibold'>{project.constructionType}</dd>
                </div>
                <div>
                  <dt className='text-neutralColor-normal'>Square Footage</dt>
                  <dd className='font-semibold'>
                    <NumberFormat value={project.squareFeet} displayType={'text'} thousandSeparator={true} suffix=' Sq.Ft ' />
                  </dd>
                </div>
              </dl>
            </div>
          </div>
          <div className='flex'>
            <Link to={`/app/administration/users/${project.projectLead.id}`} className='flex items-center gap-2'>
              <ImageCircularAvatar
                customClass='h-16 w-16'
                imageUrl={project.projectLead.profileImageUrl}
                fallbackText={getInitials(project.projectLead.name)}
              />
              <div>
                <p className='font-bold text-md'>Project Lead</p>
                <p>{project.projectLead.name}</p>
              </div>
            </Link>
          </div>
        </div>
      )}
      {fetchProject.isLoading && <div className='w-full h-48 my-4 rounded animate-pulse bg-neutralColor-lighter'></div>}
    </>
  );
};

export default ProjectInfo;
